if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();
      const submittedQuantity = this.normalizeQuantity();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');

      // Safely handle spinner for theme variants that omit the loading element.
      const spinner = this.querySelector('.loading-overlay__spinner');
      if (spinner) {
        spinner.classList.remove('hidden');
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      const formData = new FormData(this.form);
      const packagePayload = this.buildPackagePayload(formData);
      if (this.cart) {
        const sections = this.cart.getSectionsToRender().map((section) => section.id);
        if (packagePayload) {
          packagePayload.sections = sections;
          packagePayload.sections_url = window.location.pathname;
        } else {
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
        }
        this.cart.setActiveElement(document.activeElement);
      }

      if (packagePayload) {
        config.body = JSON.stringify(packagePayload);
      } else {
        delete config.headers['Content-Type'];
        config.body = formData;
      }

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          }

          this.dispatchEvent(new CustomEvent('ssc:product-added-to-cart', {
            bubbles: true,
            detail: {
              variantId: String(formData.get('id') || ''),
              quantity: submittedQuantity
            }
          }));

          if (packagePayload?.items?.length > 1) {
            const baseItem = packagePayload.items[packagePayload.items.length - 1];
            const addonItems = packagePayload.items.slice(0, -1);
            const addonProductHandles = Array.from(new Set([
              ...Array.from(
                this.form.querySelectorAll('[data-package-addon]:checked')
              ).map(input => input.dataset.packageAddonProductHandle),
              ...Array.from(
                this.form.querySelectorAll('[data-package-addon-select]')
              ).filter(select => select.value && !select.disabled)
                .map(select => select.dataset.packageAddonProductHandle)
            ].filter(Boolean)));
            window.sscTrackPackageConfigurator?.('package_configurator_add_to_cart', {
              base_variant_id: String(baseItem.id),
              addon_count: addonItems.length,
              addon_unit_count: addonItems.reduce(
                (sum, item) => sum + (Number(item.quantity) || 0),
                0
              ),
              addon_variant_ids: addonItems.map(item => String(item.id)),
              addon_product_handles: addonProductHandles
            });
          }

          if (!this.cart) {
            // Instead of redirecting, show simple success message
            const productTitle = document.querySelector('.product-title, h1')?.textContent?.trim() || 'Item';

            // Try to show toast notification
            try {
              if (typeof window.showCartToast === 'function') {
                window.showCartToast(`${productTitle} added to cart!`);
              }
            } catch (e) {
              console.log('Toast notification failed:', e);
            }

            // Try to update cart count
            try {
              if (typeof window.updateCartCount === 'function') {
                window.updateCartCount();
              }
            } catch (e) {
              console.log('Cart count update failed:', e);
            }

            // Fallback: Dispatch custom event for other listeners
            try {
              document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { product: productTitle }
              }));
            } catch (e) {
              // Older browser fallback
              const event = document.createEvent('CustomEvent');
              event.initCustomEvent('cart:updated', true, true, { product: productTitle });
              document.dispatchEvent(event);
            }

            return;
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');

          // Safely handle spinner for theme variants that omit the loading element.
          const spinner = this.querySelector('.loading-overlay__spinner');
          if (spinner) {
            spinner.classList.add('hidden');
          }
        });
    }

    buildPackagePayload(formData) {
      const configurator = this.form.querySelector('[data-package-configurator]');
      if (!configurator) return null;

      const variantId = formData.get('id');
      if (!variantId) return null;

      const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
      const configurationId = this.createConfigurationId();
      const selectedCheckboxAddons = Array.from(
        configurator.querySelectorAll('[data-package-addon]:checked')
      ).map((input) => ({
        id: String(input.dataset.variantId),
        title: input.dataset.title,
        quantity: this.getPackageAddonQuantity(input, quantity)
      }));
      const selectedSelectAddons = Array.from(
        configurator.querySelectorAll('[data-package-addon-select]')
      ).filter((select) => select.value && !select.disabled)
        .map((select) => {
          const option = select.options[select.selectedIndex];
          return {
            id: String(select.value),
            title: option?.dataset.title || option?.textContent?.trim() || 'Package add-on',
            quantity
          };
        });
      const selectedAddons = Array.from(
        [...selectedCheckboxAddons, ...selectedSelectAddons].reduce(
          (addonsById, addon) => addonsById.set(addon.id, addon),
          new Map()
        ).values()
      );
      const baseProperties = this.getLineItemProperties(formData);
      const baseTitle = configurator.dataset.baseProductTitle || 'Configured product';
      const baseUrl = configurator.dataset.baseProductUrl || '';
      const packageParentUrl = baseUrl
        ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}variant=${encodeURIComponent(variantId)}`
        : '';

      baseProperties._configuration_id = configurationId;
      if (selectedAddons.length) {
        baseProperties['Package accessories'] = selectedAddons
          .map((addon) => addon.quantity > 1 ? `${addon.title} x ${addon.quantity}` : addon.title)
          .join(', ');
      }

      const baseItem = {
        id: String(variantId),
        quantity,
        properties: baseProperties
      };
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) baseItem.selling_plan = String(sellingPlan);

      const addonItems = selectedAddons.map((addon) => {
        const properties = {
          _configuration_id: configurationId,
          Package: baseTitle
        };
        if (packageParentUrl) properties._package_parent_url = packageParentUrl;

        return {
          id: addon.id,
          quantity: addon.quantity,
          properties
        };
      });

      return {
        items: [
          ...addonItems.reverse(),
          baseItem
        ]
      };
    }

    getPackageAddonQuantity(input, fallbackQuantity) {
      const quantityInput = input
        .closest('.package-addon')
        ?.querySelector('[data-package-addon-quantity]');

      if (!quantityInput) return fallbackQuantity;

      const parsedQuantity = Math.floor(Number(quantityInput.value));
      return Number.isFinite(parsedQuantity) && parsedQuantity > 0
        ? parsedQuantity
        : 1;
    }

    normalizeQuantity() {
      const quantityInput = this.form.querySelector('[name="quantity"]');
      if (!quantityInput) return 1;

      const minimumQuantity = Math.max(1, Math.floor(Number(quantityInput.min)) || 1);
      const requestedQuantity = Math.floor(Number(quantityInput.value));
      const normalizedQuantity = Number.isFinite(requestedQuantity)
        ? Math.max(minimumQuantity, requestedQuantity)
        : minimumQuantity;

      quantityInput.value = String(normalizedQuantity);
      return normalizedQuantity;
    }

    getLineItemProperties(formData) {
      const properties = {};

      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (!match || value === '') continue;
        if (typeof File !== 'undefined' && value instanceof File) continue;
        properties[match[1]] = String(value);
      }

      return properties;
    }

    createConfigurationId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();

      return `package-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }


  });
}

(function () {
  let uploaderyLayoutScheduled = false;

  function isHydratedUploaderyWrapper(wrapper) {
    return Boolean(
      wrapper &&
      (
        wrapper.querySelector('input[type="file"]') ||
        wrapper.querySelector('[id*="uploadery"]:not(:empty)') ||
        wrapper.querySelector('[class*="uploadery"]:not(:empty)')
      )
    );
  }

  function getActiveUploaderyWrapper(wrappers) {
    return wrappers.find(function (wrapper) {
      const rect = wrapper.getBoundingClientRect();
      const style = window.getComputedStyle(wrapper);

      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        rect.width > 0
      );
    });
  }

  function syncProductUploaderyLayout() {
    uploaderyLayoutScheduled = false;

    const desktopInfo = document.querySelector('.product-info-desktop .product-info-content');

    if (desktopInfo) {
      const productForm = desktopInfo.querySelector(':scope > .product-form-container');
      const shippingInfo = desktopInfo.querySelector(':scope > .shipping-info-section');

      if (productForm && shippingInfo && productForm.nextElementSibling !== shippingInfo) {
        productForm.insertAdjacentElement('afterend', shippingInfo);
      }
    }

    const wrappers = Array.from(document.querySelectorAll('[data-uploadery-wrapper]'));
    if (wrappers.length < 2) return;

    const targetWrapper = getActiveUploaderyWrapper(wrappers);
    const hydratedWrapper = wrappers.find(isHydratedUploaderyWrapper);

    if (!targetWrapper || !hydratedWrapper || targetWrapper === hydratedWrapper) return;

    Array.from(hydratedWrapper.childNodes).forEach(function (node) {
      targetWrapper.appendChild(node);
    });
  }

  function scheduleProductUploaderyLayoutSync() {
    if (uploaderyLayoutScheduled) return;

    uploaderyLayoutScheduled = true;
    window.requestAnimationFrame(syncProductUploaderyLayout);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleProductUploaderyLayoutSync);
  } else {
    scheduleProductUploaderyLayoutSync();
  }

  window.addEventListener('load', scheduleProductUploaderyLayoutSync);
  window.addEventListener('resize', scheduleProductUploaderyLayoutSync);

  if (window.MutationObserver) {
    const observerRoot = document.querySelector('.product-page') || document.body;

    new MutationObserver(scheduleProductUploaderyLayoutSync).observe(observerRoot, {
      childList: true,
      subtree: true
    });
  }

  window.setTimeout(scheduleProductUploaderyLayoutSync, 750);
  window.setTimeout(scheduleProductUploaderyLayoutSync, 2000);
})();
